import axios from 'axios';

// Defensively strip a trailing slash so URLs never become `https://host//api/...`.
// Double-slash paths are routed differently by production CDNs/ingress (Cloudflare
// served the React index.html for `//api/categories`, breaking the entire app).
const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const BACKEND_URL = RAW_BACKEND_URL.replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

// Create axios instance with auth header
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('lebonlux_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('lebonlux_token');
      localStorage.removeItem('lebonlux_user');
      // Don't redirect automatically, let components handle it
    }
    return Promise.reject(error);
  }
);

// ============ AUTH API ============
export const phoneVerificationAPI = {
  status: async () => (await api.get('/verification/phone/status')).data,
  start: async (phone, country) => (await api.post('/verification/phone/start', { phone, country })).data,
  check: async (code) => (await api.post('/verification/phone/check', { code })).data,
};

export const authAPI = {
  register: async (name, email, password) => {
    const response = await api.post('/auth/register', { name, email, password });
    return response.data;
  },
  
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
  
  updateMe: async (updates) => {
    const response = await api.put('/auth/me', updates);
    return response.data;
  },
  
  getUser: async (userId) => {
    const response = await api.get(`/auth/user/${userId}`);
    return response.data;
  }
};

// ============ SEARCH API ============
export const searchAPI = {
  trending: async (limit = 8) => {
    const response = await api.get('/search/trending', { params: { limit } });
    return response.data;
  },
};

// ============ LISTINGS API ============
export const listingsAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/listings', { params });
    return response.data;
  },

  getFacets: async (params = {}) => {
    const response = await api.get('/listings/facets', { params });
    return response.data;
  },
  
  // Fast cached endpoint for homepage - can skip images for faster initial load
  getHomepage: async (limit = 20, includeImages = true) => {
    const response = await api.get('/listings/homepage', { 
      params: { limit, include_images: includeImages } 
    });
    return response.data;
  },
  
  // Get single image for lazy loading
  getListingImage: async (listingId, index = 0) => {
    const response = await api.get(`/listings/${listingId}/image`, { params: { index } });
    return response.data.image;
  },

  // Typeahead suggestions for global search bar
  suggest: async (q, limit = 8) => {
    const response = await api.get('/listings/suggest', { params: { q, limit } });
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/listings/${id}`);
    return response.data;
  },
  
  create: async (listingData) => {
    const response = await api.post('/listings', listingData);
    return response.data;
  },
  
  update: async (id, updates) => {
    const response = await api.put(`/listings/${id}`, updates);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/listings/${id}`);
    return response.data;
  },
  
  getByUser: async (userId, params = {}) => {
    const response = await api.get(`/listings/user/${userId}`, { params });
    return response.data;
  },
  
  markAsSold: async (id) => {
    const response = await api.post(`/listings/${id}/sold`);
    return response.data;
  },
  
  reactivate: async (id) => {
    const response = await api.post(`/listings/${id}/reactivate`);
    return response.data;
  }
};

// ============ CATEGORIES API ============
export const categoriesAPI = {
  getAll: async () => {
    const response = await api.get('/categories');
    return response.data;
  }
};

// ============ USER API ============
export const userAPI = {
  getById: async (userId) => {
    const response = await api.get(`/users/${userId}`);
    return response.data;
  },
  
  uploadAvatar: async (base64Image) => {
    const response = await api.post('/users/avatar', { image: base64Image });
    return response.data;
  }
};

// ============ LOCATIONS API ============
export const locationsAPI = {
  getAll: async () => {
    const response = await api.get('/locations');
    return response.data;
  },
  
  // P2: Updated to use Google Maps-powered autocomplete
  search: async (query, limit = 10) => {
    try {
      // Try new Google Maps autocomplete endpoint first
      const response = await api.get('/locations/autocomplete', { params: { q: query, limit } });
      return response.data;
    } catch (error) {
      // Fallback to old endpoint if new one fails
      console.warn('Google Maps autocomplete failed, using fallback:', error);
      const response = await api.get('/locations/search', { params: { q: query, limit } });
      return response.data;
    }
  },
  
  // P2: Get detailed place info from place_id
  getPlaceDetails: async (placeId) => {
    const response = await api.get(`/locations/details/${placeId}`);
    return response.data;
  },
  
  // P2: Geocode an address to coordinates
  geocode: async (address) => {
    const response = await api.post('/locations/geocode', null, { params: { address } });
    return response.data;
  },
  
  getNearest: async (lat, lng) => {
    const response = await api.post('/locations/nearest', { lat, lng });
    return response.data;
  }
};

// ============ FAVORITES API ============
export const personalizationAPI = {
  event: (payload) => api.post('/personalization/events', payload).catch(() => {}),
  hide: async (listingId) => (await api.post(`/personalization/hide/${listingId}`)).data,
  unhide: async (listingId) => (await api.delete(`/personalization/hide/${listingId}`)).data,
  profile: async () => (await api.get('/personalization/profile')).data,
  setEnabled: async (personalized) => (await api.put('/personalization/settings', { personalized })).data,
  clearHistory: async () => (await api.delete('/personalization/events')).data,
};

export const favoritesAPI = {
  getAll: async () => {
    const response = await api.get('/favorites');
    return response.data;
  },
  
  getIds: async () => {
    const response = await api.get('/favorites/ids');
    return response.data;
  },
  
  add: async (listingId) => {
    const response = await api.post(`/favorites/${listingId}`);
    return response.data;
  },
  
  remove: async (listingId) => {
    const response = await api.delete(`/favorites/${listingId}`);
    return response.data;
  },
  
  check: async (listingId) => {
    const response = await api.get(`/favorites/check/${listingId}`);
    return response.data;
  }
};

// ============ CONVERSATIONS API ============
export const conversationsAPI = {
  getAll: async () => {
    const response = await api.get('/conversations');
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/conversations/${id}`);
    return response.data;
  },
  
  create: async (listingId, otherUserId, initialMessage, offerAmount = null) => {
    const payload = { listing_id: listingId, other_user_id: otherUserId, initial_message: initialMessage || null };
    if (offerAmount) payload.offer_amount = offerAmount;
    const response = await api.post('/conversations', payload);
    return response.data;
  },
  
  sendMessage: async (conversationId, text, attachments = null, offerAmount = null) => {
    const data = { text: text || '' };
    if (attachments) {
      data.attachments = attachments;
    }
    if (offerAmount) data.offer_amount = offerAmount;
    const response = await api.post(`/conversations/${conversationId}/messages`, data);
    return response.data;
  },

  respondToOffer: async (conversationId, messageId, action) => {
    const response = await api.post(`/conversations/${conversationId}/offers/${messageId}/respond`, { action });
    return response.data;
  },

  answerFollowup: async (conversationId, messageId, answer) => {
    const response = await api.post(`/conversations/${conversationId}/followup/${messageId}/answer`, { answer });
    return response.data;
  },
  
  markAsRead: async (conversationId) => {
    const response = await api.post(`/conversations/${conversationId}/read`);
    return response.data;
  },
  
  markAllAsRead: async () => {
    const response = await api.post('/conversations/mark-all-read');
    return response.data;
  }
};

// ============ REPORTS API ============
export const reportsAPI = {
  create: async (data) => {
    const response = await api.post('/reports', data);
    return response.data;
  },
  
  getAll: async (statusFilter = null) => {
    const params = statusFilter ? { status_filter: statusFilter } : {};
    const response = await api.get('/reports', { params });
    return response.data;
  },
  
  resolve: async (reportId, action, adminNotes = null) => {
    const params = { action };
    if (adminNotes) params.admin_notes = adminNotes;
    const response = await api.put(`/reports/${reportId}/resolve`, null, { params });
    return response.data;
  },
  
  getStats: async () => {
    const response = await api.get('/reports/stats');
    return response.data;
  }
};

// ============ AI API ============
export const aiAPI = {
  analyzeImage: async (base64Image, categoryHint = null) => {
    const payload = { image_base64: base64Image };
    if (categoryHint) {
      payload.category = categoryHint;
    }
    const response = await api.post('/ai/analyze', payload);
    return response.data;
  },
  
  analyzeText: async (title, description, categoryHint = null) => {
    const payload = { title, description };
    if (categoryHint) {
      payload.category = categoryHint;
    }
    const response = await api.post('/ai/analyze-text', payload);
    return response.data;
  },
  
  translate: async (text, sourceLanguage, targetLanguage) => {
    const response = await api.post('/ai/translate', {
      text,
      source_language: sourceLanguage,
      target_language: targetLanguage
    });
    return response.data;
  }
};

// ============ UPLOAD API ============
export const uploadAPI = {
  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }
};

// ============ ADMIN API ============
export const adminAPI = {
  getStats: async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  },
  
  getUsers: async (skip = 0, limit = 50) => {
    const response = await api.get('/admin/users', { params: { skip, limit } });
    return response.data;
  },
  
  getUserDetails: async (userId) => {
    const response = await api.get(`/admin/users/${userId}`);
    return response.data;
  },
  
  deleteUser: async (userId) => {
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  },
  
  makeAdmin: async (userId) => {
    const response = await api.post(`/admin/make-admin/${userId}`);
    return response.data;
  },
  
  getListings: async (skip = 0, limit = 50, category = null, search = null) => {
    const params = { skip, limit };
    if (category) params.category = category;
    if (search) params.search = search;
    const response = await api.get('/admin/listings', { params });
    return response.data;
  },
  
  updateListing: async (listingId, data) => {
    const response = await api.put(`/admin/listings/${listingId}`, data);
    return response.data;
  },
  
  deleteListing: async (listingId) => {
    const response = await api.delete(`/admin/listings/${listingId}`);
    return response.data;
  }
};

// Import Listing API
export const importAPI = {
  fromUrl: async (url) => {
    const response = await api.post('/import/from-url', { url });
    return response.data;
  },
  
  getSupportedPlatforms: async () => {
    const response = await api.get('/import/supported-platforms');
    return response.data;
  }
};

// ============ SAVED SEARCHES API ============
export const savedSearchesAPI = {
  // Create a new saved search
  create: async (name, filters, emailAlerts = true) => {
    const response = await api.post('/saved-searches', {
      name,
      filters,
      email_alerts: emailAlerts
    });
    return response.data;
  },

  // Get all saved searches for current user
  getAll: async () => {
    const response = await api.get('/saved-searches');
    return response.data;
  },

  // Get a specific saved search
  get: async (searchId) => {
    const response = await api.get(`/saved-searches/${searchId}`);
    return response.data;
  },

  // Update a saved search (name / alert frequency)
  update: async (searchId, { name, emailAlerts, alertFrequency }) => {
    const payload = {};
    if (name !== undefined) payload.name = name;
    if (alertFrequency !== undefined) payload.alert_frequency = alertFrequency;
    else if (emailAlerts !== undefined) payload.email_alerts = emailAlerts;
    const response = await api.put(`/saved-searches/${searchId}`, payload);
    return response.data;
  },

  // Total unseen matches across all saved searches (nav badge)
  newCount: async () => {
    const response = await api.get('/saved-searches/new-count');
    return response.data;
  },

  // Reset the new-matches badge for one search
  markViewed: async (searchId) => {
    const response = await api.post(`/saved-searches/${searchId}/viewed`);
    return response.data;
  },

  // Delete a saved search
  delete: async (searchId) => {
    const response = await api.delete(`/saved-searches/${searchId}`);
    return response.data;
  },

  // Run a saved search and get matching listings
  run: async (searchId) => {
    const response = await api.post(`/saved-searches/${searchId}/run`);
    return response.data;
  }
};

export default api;
